import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import { supabase } from '../../utils/supabaseClient';

const CitizenGuides = () => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Static categories that map well to typical government services
  const categories = [
    { key: 'all', label: 'ทั้งหมด' },
    { key: 'id-card', label: 'บัตรประชาชน' },
    { key: 'civil-registry', label: 'ทะเบียนราษฎร' },
    { key: 'general-registry', label: 'ทะเบียนทั่วไป' },
    { key: 'firearms', label: 'อาวุธปืน' },
    { key: 'gambling', label: 'การพนัน' },
    { key: 'auction-antique', label: 'ขายทอดตลาดและค้าของเก่า' },
    { key: 'foundation', label: 'มูลนิธิ' },
    { key: 'association', label: 'สมาคม' },
    { key: 'donation', label: 'เรี่ยไร' },
    { key: 'hotel', label: 'โรงแรม' },
    { key: 'service-place', label: 'สถานบริการ' },
    { key: 'pawnshop', label: 'โรงรับจำนำ' },
    { key: 'border-pass', label: 'สัญจรข้ามแดน' },
    { key: 'minority', label: 'ชนกลุ่มน้อย' },
    { key: 'nationality', label: 'สัญชาติ' },
    { key: 'other', label: 'อื่นๆ' }
  ];

  // External links for each category (open in new tab when clicked)
  const categoryExternalLinks = {
    'id-card': 'https://www.dopa.go.th/public_service/service_guide23',
    'civil-registry': 'https://www.dopa.go.th/public_service/service_guide24',
    'general-registry': 'https://www.dopa.go.th/public_service/service_guide230',
    'firearms': 'https://www.dopa.go.th/public_service/service_guide286',
    'gambling': 'https://www.dopa.go.th/public_service/service_guide306',
    'auction-antique': 'https://www.dopa.go.th/public_service/service_guide349',
    'foundation': 'https://www.dopa.go.th/public_service/service_guide357',
    'association': 'https://www.dopa.go.th/public_service/service_guide362',
    'donation': 'https://www.dopa.go.th/public_service/service_guide367',
    'hotel': 'https://www.dopa.go.th/public_service/service_guide371',
    'service-place': 'https://www.dopa.go.th/public_service/service_guide383',
    'pawnshop': 'https://www.dopa.go.th/public_service/service_guide393',
    'border-pass': 'https://www.dopa.go.th/public_service/service_guide404',
    'minority': 'https://www.dopa.go.th/public_service/service_guide407',
    'nationality': 'https://www.dopa.go.th/public_service/service_guide416'
  };

  // Tiles shown at the top (visual like sample image)
  const categoryTiles = [
    { key: 'id-card', label: 'ทะเบียนบัตรประจำตัวประชาชน' },
    { key: 'civil-registry', label: 'ทะเบียนราษฎร' },
    { key: 'general-registry', label: 'ทะเบียนทั่วไป' },
    { key: 'firearms', label: 'อาวุธปืน' },
    { key: 'gambling', label: 'การพนัน' },
    { key: 'auction-antique', label: 'ขายทอดตลาดและค้าของเก่า' },
    { key: 'foundation', label: 'มูลนิธิ' },
    { key: 'association', label: 'สมาคม' },
    { key: 'donation', label: 'เรี่ยไร' },
    { key: 'hotel', label: 'โรงแรม' },
    { key: 'service-place', label: 'สถานบริการ' },
    { key: 'other', label: 'อื่นๆ' },
    { key: 'pawnshop', label: 'โรงรับจำนำ' },
    { key: 'border-pass', label: 'สัญจรข้ามแดน' },
    { key: 'minority', label: 'ชนกลุ่มน้อย' },
    { key: 'nationality', label: 'สัญชาติ' }
  ];

  useEffect(() => {
    let isMounted = true;
    async function fetchGuides() {
      setLoading(true);
      setError('');
      try {
        const { data, error: err } = await supabase
          .from('citizen_guides')
          .select('*')
          .order('updated_at', { ascending: false });
        if (err) throw err;
        if (isMounted) setGuides(data || []);
      } catch (e) {
        // If table does not exist yet, fallback to empty list
        if (isMounted) setGuides([]);
        if (isMounted) setError('');
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    fetchGuides();
    return () => {
      isMounted = false;
    };
  }, []);

  const filteredGuides = useMemo(() => {
    let list = [...guides];
    if (category !== 'all') {
      list = list.filter((g) => g.category === category);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (g) =>
          g.title?.toLowerCase()?.includes(q) ||
          g.department?.toLowerCase()?.includes(q) ||
          g.summary?.toLowerCase()?.includes(q)
      );
    }
    return list;
  }, [guides, category, search]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>คู่มือประชาชน - อำเภอเทิง</title>
        <meta name="description" content="ดาวน์โหลดและค้นหาคู่มือประชาชน แบ่งตามหมวดหมู่บริการต่างๆ" />
      </Helmet>
      <Header />
      <main className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        <BreadcrumbTrail />

        <div className="mb-6 flex items-center space-x-3">
          <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
            <Icon name="Book" size={20} className="text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-foreground">คู่มือประชาชน</h1>
            <p className="text-muted-foreground mt-1">ค้นหา ดาวน์โหลด และศึกษาแนวทางการขอรับบริการภาครัฐ</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filters */}
          <aside className="lg:col-span-1">
            <div className="bg-card border border-border rounded-lg p-4 shadow-government space-y-4">
              <div>
                <div className="text-sm font-semibold mb-2">หมวดหมู่</div>
                <div className="space-y-2">
                  {categories.map((c) => (
                    categoryExternalLinks[c.key] ? (
                      <a
                        key={c.key}
                        href={categoryExternalLinks[c.key]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`block px-3 py-2 rounded-md border transition-government ${
                          'bg-muted/30 border-border text-foreground hover:bg-primary/10'
                        }`}
                        title={`เปิด: ${c.label}`}
                      >
                        {c.label}
                      </a>
                    ) : (
                      <button
                        key={c.key}
                        onClick={() => setCategory(c.key)}
                        className={`w-full text-left px-3 py-2 rounded-md border transition-government ${
                          category === c.key
                            ? 'bg-primary/10 border-primary text-foreground'
                            : 'bg-muted/30 border-border text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {c.label}
                      </button>
                    )
                  ))}
                </div>
              </div>
            </div>
          </aside>

        </div>
      </main>
    </div>
  );
};

export default CitizenGuides;


